<template>
  <div class="popup" :style="{ left: `${x}px`, top: `${y}px` }" :class="`popup--${position}`" ref="popup">
    <div v-if="isLoading" class="popup__tools">
      <div class="select select--default select--loading">
        <span class="select__loading">Loading...</span>
      </div>
    </div>
    <div v-else>
      <p v-if="type == 'policyFirstAgent'" class="agent-label">{{ $t(`addPolicyModal.${'agent'}`) }}</p>
      <p v-if="type == 'policySecondAgent'" class="agent-label"> {{ $t(`addSaleCategoryModal.${"secondAgent"}`) }}</p>
      

      <div class="popup__tools">
        <div v-if="type != 'confirm'" class="popup__inputs">
          <VSelect v-if="type == 'policySecondAgent' && items" class="select select--default" :options="items"
            :reduce="(item) => item.id" :getOptionLabel="(item) => `${item.first_name} ${item.last_name}`"
            :placeholder="selected" v-model="value" ref="vselect" />
          <VSelect v-else-if="items && Array.isArray(label)" class="select select--default" :options="items"
            :reduce="(item) => item.id" :getOptionLabel="(item) => `${item[label[0]]} ${item[label[1]]}`"
            :placeholder="selected" v-model="value" ref="vselect" />
          <VSelect v-else-if="items" class="select select--default" :options="items" :reduce="(item) => item.id"
            :label="label" :placeholder="selected" v-model="value" ref="vselect" />
          <Datepicker v-else-if="isDate" class="input input--default" ref="datepicker"
            :config="statics.datePickerConfig" v-model="value" />
          <input v-else-if="isPrice" type="number" min="0" step=".01" class="input input--default" :value="selected"
            @input="value = $event.target.value" ref="priceInput" />
          <input v-else type="text" class="input input--default" :value="selected" @input="value = $event.target.value"
            ref="textInput" /> 

          <VSelect v-if="type == 'policySecondAgent'"
            class="select select--default" :options="secondAgentCategoryOptions"
            :reduce="(item) => item" :getOptionLabel="(item) => item?.name || ''"
            :placeholder="secondaryValue ? secondaryValue.name : 'Select category'"
            v-model="secondaryValue" />
        </div>
        <p v-else-if="type == 'confirm'" class="agent-label">{{ confirmText }}</p>
        <div class="popup__button popup__button--confirm"
          :class="{ 'popup__button--confirm-secondAgent': type == 'policySecondAgent' }" @click="submit()"></div>
        <div class="popup__button popup__button--cancel"
          :class="{ 'popup__button--cancel-secondAgent': type == 'policySecondAgent' }" @click="close()"></div>
        <div v-if="type == 'policySecondAgent'" class="popup__button popup__button--delete"
          @click="deleteSecondAgent()"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from "vue";
import { useStore } from "vuex";
import statics from "@/statics";
import { formatDisplayDate } from "@/helpers/dateFormatters";

const store = useStore();

const props = defineProps({
  selected: {
    required: false,
    type: [String, Number],
  },
  items: {
    required: false,
    type: Array,
  },
  position: {
    required: false,
    type: String,
  },
  label: {
    required: false,
    type: [String, Array],
  },
  isDate: {
    required: false,
    type: Boolean,
  },
  isPrice: {
    required: false,
    type: Boolean,
  },
  allowNegative: {
    required: false,
    type: Boolean,
  },
  isLoading: {
    required: false,
    type: Boolean,
    default: false,
  },
  type: {
    required: false,
    type: String,
    default: "",
  },
  secondarySelected: {
    required: false,
    type: [String, Number],
  },
  confirmText: {
    required: false,
    type: String,
    default: "Are you sure?",
  }
});

const emit = defineEmits(["togglePopup", "submit", "deleteSecondAgent"]);

const value = ref("");
const secondaryValue = ref(null);
const initialSecondAgentId = ref(null);
const secondAgentInitialized = ref(false);
const x = ref(0);
const y = ref(0);

const selectedSecondAgentId = computed(() => {
  if (props.type !== "policySecondAgent") {
    return null;
  }

  if (value.value) {
    return Number(value.value);
  }

  const list = Array.isArray(props.items) ? props.items : [];
  const selectedName = typeof props.selected === "string" ? props.selected.trim() : "";

  if (!list.length) {
    return null;
  }

  const matchedAgent = list.find((agentItem) => {
    const fullName = `${agentItem.first_name || ""} ${agentItem.last_name || ""}`.trim();
    return Number(agentItem.id) === Number(props.selected) || fullName === selectedName;
  });

  return matchedAgent ? Number(matchedAgent.id) : null;
}); 
 

const secondAgentCategoryOptions = computed(() => {
  if (props.type !== "policySecondAgent") {
    return [];
  }

  const selectedAgentId = selectedSecondAgentId.value;
  const selectedAgent = (Array.isArray(props.items) ? props.items : []).find(
    (agentItem) => Number(agentItem.id) === Number(selectedAgentId)
  );

  const categoryCandidates = [
    selectedAgent?.second_sales_category,
    selectedAgent?.sales_category,
    selectedAgent?.secondAgentSalesCategory,
    selectedAgent?.second_agent_sales_category,
  ];

  for (const candidate of categoryCandidates) {
    if (Array.isArray(candidate) && candidate.length) {
      return candidate;
    }
  }

  return [];
});

const resolvePreselectedSecondAgent = () => {
  if (props.type !== "policySecondAgent" || !Array.isArray(props.items) || !props.selected) {
    return;
  }

  if (initialSecondAgentId.value !== null && value.value && Number(value.value) !== Number(initialSecondAgentId.value)) {
    return;
  }

  const selectedAgentName = typeof props.selected === "string" ? props.selected.trim() : "";
  const matchedAgent = props.items.find((agentItem) => {
    const fullName = `${agentItem.first_name || ""} ${agentItem.last_name || ""}`.trim();
    return Number(agentItem.id) === Number(props.selected) || fullName === selectedAgentName;
  });

  if (matchedAgent) {
    value.value = matchedAgent.id;
    initialSecondAgentId.value = Number(matchedAgent.id);
  }
};

const initializeSecondAgent = () => {
  if (props.type !== "policySecondAgent") return;

  resolvePreselectedSecondAgent();

  const categoryId = props.secondarySelected;

  if (categoryId) {
    const matchingCategory = secondAgentCategoryOptions.value.find(
      (category) => Number(category.id) === Number(categoryId)
    );

    secondaryValue.value = matchingCategory ?? null;
  } else {
    secondaryValue.value = null;
  }

  secondAgentInitialized.value = true;
};

watch(
  () => value.value,
  (newAgentId, oldAgentId) => {
    if (props.type !== "policySecondAgent") return;
    if (!secondAgentInitialized.value) return;

    if (
      oldAgentId !== null &&
      oldAgentId !== "" &&
      Number(newAgentId) !== Number(oldAgentId)
    ) {
      secondaryValue.value = null;
    }
  }
);

const deleteSecondAgent = () => {
  emit("deleteSecondAgent");
  close();
};

// Template refs
const popup = ref(null);
const vselect = ref(null);
const datepicker = ref(null);
const priceInput = ref(null);
const textInput = ref(null);

const event = (ev) => {
  const { target } = ev;
  const popupEl = popup.value;

  if (
    target === popupEl ||
    target === popupEl.parentElement ||
    [...popupEl.getElementsByTagName("*")].includes(target)
  ) {
    emit("togglePopup", true);
  } else if (
    document.querySelector(".flatpickr-calendar") &&
    [
      ...document
        .querySelector(".flatpickr-calendar")
        .getElementsByTagName("*"),
    ].includes(target)
  ) {
    emit("togglePopup", true);
  } else {
    close();
  }
};

const submit = () => {
  if (props.type === "policySecondAgent") {
    resolvePreselectedSecondAgent();

    if (!value.value) {
      store.commit("setToast", {
        display: true,
        type: "error",
        message: "Please select a second agent.",
      });
      return;
    }

    if (!secondAgentCategoryOptions.value.length) {
      store.commit("setToast", {
        display: true,
        type: "error",
        message: "This second agent has no sales categories available.",
      });
      return;
    }

    const selectedCategoryId = Number(secondaryValue.value?.id ?? secondaryValue.value ?? 0);
    if (!selectedCategoryId) {
      store.commit("setToast", {
        display: true,
        type: "error",
        message: "Please provide a second agent category.",
      });
      return;
    }

    emit("submit", {
      second_agent_id: Number(value.value),
      second_agent_sales_category_id: selectedCategoryId,
    });
    close();
    return;
  }

  if (props.type === "confirm") {
    emit("submit", true);
    close();
    return;
  }

  if (props.isPrice && !props.allowNegative && (isNaN(value.value) || value.value <= 0)) {
    alert("Please enter a valid price greater than 0.");
  } else {
    emit("submit", value.value);
    close();
  }
};

const computePopupLocation = () => {
  const popupEl = popup.value;
  x.value =
    popupEl.parentElement.getBoundingClientRect().left +
    popupEl.parentElement.getBoundingClientRect().width +
    10;
  y.value = popupEl.parentElement.getBoundingClientRect().top - 20;

  nextTick(() => {
    const popupRect = popupEl.getBoundingClientRect();

    const isOutside = popupRect.right > window.innerWidth;

    if (isOutside) {
      x.value =
        popupEl.parentElement.getBoundingClientRect().left -
        popupRect.width -
        10;
    }
  });
};

const close = () => {
  emit("togglePopup", false);
};

onMounted(() => {
  computePopupLocation();
  document.addEventListener("click", event);

  if (props.type === "policySecondAgent") {
    initializeSecondAgent();
  } else if (!props.items) {
    value.value = props.selected;
  }

  if (props.isDate) {
    datepicker.value.$el.value = formatDisplayDate(props.selected);
  }

  nextTick(() => {
    computePopupLocation();
  });
});

onUnmounted(() => {
  document.removeEventListener("click", event);
});
</script>

<style scoped lang="scss">
.agent-label {
  margin-bottom: 10px;
  font-size: var(--small);
  color: var(--text-9);
}

// .confirm-label {
//   max-width: 220px;
//   margin-bottom: 12px;
//   line-height: 1.4;
// }

.popup {
  position: fixed;
  z-index: 3;
  width: 320px;
  border-radius: var(--radius-1);
  border: 1px solid var(--border-2);
  background: var(--bg-1);
  box-shadow: 0px 3px 6px var(--shadow-7);
  padding: 10px 20px;
  cursor: default;

  &--left {
    left: initial;
    right: calc(100% + 10px);
  }

  .select {
    min-width: 200px;
    max-width: 200px;

    &--loading {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 38px;
      border: 1px solid var(--border-2);
      border-radius: var(--radius-1);
      background: var(--bg-2);
      color: white;
      font-size: 14px;
      min-width: 100%;

      &::after {
        display: none;
      }
    }
  }

  .select__loading {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__tools {
    display: flex;
    align-items: center;

  }

  &__inputs {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__button {
    min-width: 24px;
    height: 24px;
    cursor: pointer;
    transition: all 0.3s ease;

    &--confirm {
      background: url("../assets/icons/default/confirm_icon.svg");
      margin: 0 15px;

      &:hover {
        background: url("../assets/icons/default/confirm_hover_icon.svg");
      }
    }

    &--confirm-secondAgent {
      margin: 0 !important;
      margin-left: 6px !important;
    }

    &--cancel {
      background: url("../assets/icons/default/close_icon.svg");

      &:hover {
        background: url("../assets/icons/default/close_hover_icon.svg");
      }
    }

    &--cancel-secondAgent {
      margin: 0 6px;
    }

    &--delete {
      background: url("../assets/icons/default/delete_icon_2.svg");
    }
  }
}
</style>
